import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import { ETags, Roles, endPoints } from 'src/enums';
import {
  createFetchArgs,
  createFetchArgsWithBody,
  parseResponse,
  userParamsToFormData,
} from '../helpers';
import {
  IQueryIdProps,
  IQueryPropsWithUserId,
  IQuerySortParams,
  responseListItems,
  TQueryId,
} from '../types';
import { TCustomFields } from '../userSlice';
import { emptySplitApi } from './api';
import { IShortClient } from './clients';
import { addMethodsType, addProviderType } from 'src/components/extensions/Settings';
import { ProviderType } from './provider';
import { TFileString } from 'src/global';

export type AccountTypes = ProviderType | addMethodsType | addProviderType;

export interface IExternalAccount {
  id: string;
  sub: string;
  family_name?: string;
  given_name?: string;
  rest_info?: string;
  type: AccountTypes;
  issuer: string;
  avatar?: string;
  profile_link?: string;
}

export interface IRoleClients {
  role: Roles;
  client: {
    name: string;
    client_id: string;
    parent_id: string | null;
  };
}

export interface IRole {
  role: Roles;
  client_id: string;
}

export interface PrivateClaims {
  public_profile_claims_oauth?: string;
  public_profile_claims_gravatar?: string;
  public_accounts_claims_oauth?: number[];
  public_accounts_claims_gravatar?: number[];
}

export interface IUserProfile extends PrivateClaims {
  id?: string;
  login?: string;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  birthdate?: string;
  phone_number?: string | null;
  password_updated_at?: string;
  picture?: TFileString;
  password_change_required?: boolean;
  custom_fields?: TCustomFields;
  ExternalAccount?: IExternalAccount[];
  Role?: IRole[];
  deleted?: string | null;
  email_public?: string | null;
  email_verified?: boolean;
  phone_number_verified?: boolean | null;
  profile_privacy?: boolean;
}

export interface IScope {
  id: number;
  client_id: string;
  scopes: string[];
  created_at: string;
  client: IShortClient;
}

export type TUser = {
  family_name: string;
  given_name: string;
  nickname: string;
  picture: string;
  id: number;
  blocked: boolean;
  deleted?: string;
};

export type TUserWithRole = {
  user: TUser;
  role: Roles;
};

export const usersApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getMeInfo: builder.query<IUserProfile, void>({
      query: () => `${endPoints.users}/me`,
      providesTags: [ETags.User, ETags.Organization],
    }),

    updateUser: builder.mutation<void, { userId: string; data: Partial<IUserProfile> }>({
      query: ({ userId, data }) =>
        createFetchArgsWithBody(`${endPoints.users}/${userId}`, 'PUT', userParamsToFormData(data)),
      invalidatesTags: [ETags.User],
    }),

    getUserScopes: builder.query<responseListItems<IScope[]>, IQueryPropsWithUserId>({
      query: ({ userId, query }) =>
        createFetchArgs<IQuerySortParams>(`${endPoints.users}/${userId}/scopes`, 'GET', query),
      transformResponse: (scopes: IScope[], meta: FetchBaseQueryMeta) =>
        parseResponse<IScope[]>(scopes, meta),
    }),

    revokeScopes: builder.mutation<boolean, IQueryIdProps>({
      query: ({ userId, clientId }) =>
        createFetchArgs<{ client_id: string }>(`${endPoints.users}/${userId}/scopes`, 'DELETE', {
          client_id: clientId,
        }),
    }),

    getUserRoles: builder.query<IRoleClients[], TQueryId>({
      query: ({ id }) => `${endPoints.users}/${id}/roles`,
      providesTags: [ETags.Organization],
    }),

    deleteUser: builder.mutation<void, TQueryId>({
      query: ({ id }) => createFetchArgs(`${endPoints.users}/${id}`, 'DELETE'),
    }),

    blockUser: builder.mutation<void, TQueryId>({
      query: ({ id }) => createFetchArgs(`${endPoints.users}/${id}/block`, 'PUT'),
    }),

    unblockUser: builder.mutation<void, TQueryId>({
      query: ({ id }) => createFetchArgs(`${endPoints.users}/${id}/unblock`, 'PUT'),
    }),

    addFavoriteClient: builder.mutation<void, IQueryIdProps>({
      query: ({ userId, clientId }) =>
        createFetchArgs<{ client_id: string }>(
          `${endPoints.users}/${userId}/favorite_clients`,
          'POST',
          {
            client_id: clientId,
          },
        ),
      invalidatesTags: [ETags.Catalog],
    }),

    deleteFavoriteClient: builder.mutation({
      query: (params: IQueryIdProps) =>
        createFetchArgs<{ client_id: string }>(
          `${endPoints.users}/${params.userId}/favorite_clients`,
          'DELETE',
          {
            client_id: params.clientId,
          },
        ),
      invalidatesTags: [ETags.Catalog],
    }),

    restoreProfile: builder.mutation<void, TQueryId>({
      query: ({ id }) => createFetchArgs(`${endPoints.users}/${id}/restore`, 'PUT'),
    }),

    deleteExternalAccount: builder.mutation<void, { userId: number; accountId: string }>({
      query: ({ userId, accountId }) =>
        createFetchArgs(`${endPoints.users}/${userId}/external_accounts/${accountId}`, 'DELETE'),
      invalidatesTags: [ETags.ExternalAccounts, ETags.PublicExternalAccounts],
    }),
    getPublicExternalAccounts: builder.query<
      IExternalAccount[],
      { client_id: string; user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `${endPoints.users}/public_external_accounts`,
        params: { user_id },
      }),
      providesTags: [ETags.PublicExternalAccounts],
    }),
    getExternalAccounts: builder.query<IExternalAccount[], string>({
      query: () => ({
        url: `/external_accounts`,
      }),
      providesTags: [ETags.ExternalAccounts],
    }),
  }),
});

export const {
  useGetMeInfoQuery,
  useLazyGetMeInfoQuery,
  useGetPublicExternalAccountsQuery,
  useGetExternalAccountsQuery,
  useUpdateUserMutation,
  useLazyGetUserScopesQuery,
  useRevokeScopesMutation,
  useGetUserRolesQuery,
  useDeleteUserMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useAddFavoriteClientMutation,
  useDeleteFavoriteClientMutation,
  useDeleteExternalAccountMutation,
  useRestoreProfileMutation,
} = usersApi;
